import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [isRTL, setIsRTL] = useState(currentLanguage === 'ar');

  // Derive RTL from current language (avoid stale state on mobile / initial load)
  const isRTLDerived = currentLanguage === 'ar';
  useEffect(() => {
    setIsRTL(isRTLDerived);
  }, [isRTLDerived]);

  // Update document direction and body class from current language (not from state)
  // so RTL applies immediately on mobile and when loading saved Arabic preference
  useEffect(() => {
    document.documentElement.dir = isRTLDerived ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
    if (isRTLDerived) {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [currentLanguage, isRTLDerived]);

  const changeLanguage = async (lng) => {
    try {
      await i18n.changeLanguage(lng);
      setCurrentLanguage(lng);
      
      // Store language preference in localStorage
      localStorage.setItem('preferred-language', lng);
      
      // Future: Send language preference to database
      // await updateUserLanguagePreference(lng);
      
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'ar' : 'en';
    changeLanguage(newLanguage);
  };

  // Load language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    if (savedLanguage && savedLanguage !== currentLanguage) {
      changeLanguage(savedLanguage);
    }
  }, [changeLanguage, currentLanguage]);

  const value = {
    currentLanguage,
    isRTL,
    changeLanguage,
    toggleLanguage,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
